import { useState, lazy, Suspense } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import FacebookSharpIcon from "@mui/icons-material/FacebookSharp";
import InstagramIcon from "@mui/icons-material/Instagram";
import type { FacebookPageLinkerDepartment } from "./types";

// Lazy load the drawer content - only loaded when user clicks the icon
const FacebookPageLinkerDrawer = lazy(() => import("./FacebookPageLinkerDrawer"));

interface FacebookPageLinkerProps {
    department: FacebookPageLinkerDepartment;
    onRefresh: () => void;
}

const FacebookPageLinker: React.FC<FacebookPageLinkerProps> = ({ department, onRefresh }) => {
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);

    const handleIconClick = () => {
        setDrawerOpen(true);
    };

    const handleClose = () => {
        setDrawerOpen(false);
    };

    return (
        <>
            <IconButton color="primary" onClick={handleIconClick}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FacebookSharpIcon
                        fontSize="large"
                        sx={{
                            color: "#1877F2",
                            filter: department.isFacebookPageExpired
                                ? "grayscale(100%)"
                                : "none",
                        }}
                    />
                    {department.hasInstagramAccount && (
                        <InstagramIcon
                            sx={{
                                fontSize: 24,
                                background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                                borderRadius: '6px',
                                color: '#fff',
                                padding: '3px',
                                filter: department.isFacebookPageExpired
                                    ? "grayscale(100%)"
                                    : "none",
                            }}
                        />
                    )}
                </Box>
            </IconButton>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleClose}
                ModalProps={{
                    keepMounted: false, // Don't keep mounted for better performance
                    disableEscapeKeyDown: true,
                    hideBackdrop: true,
                    onBackdropClick: (e) => e.stopPropagation(),
                }}
            >
                {drawerOpen && (
                    <Suspense 
                        fallback={
                            <Box 
                                sx={{ 
                                    width: 350,
                                    maxWidth: '90vw',
                                    height: '100%', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center' 
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        }
                    >
                        <FacebookPageLinkerDrawer
                            department={department}
                            onClose={handleClose}
                            onRefresh={onRefresh}
                        />
                    </Suspense>
                )}
            </Drawer>
        </>
    );
};

export default FacebookPageLinker;
